import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';

import moment from 'moment-timezone';

import { TimeService } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService } from '@core/i18n-legacy';
import { DatePickerComponent, TimeZonePickerComponent } from '@core/time-ui';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  DialogRef,
  DropdownComponent,
  ButtonComponent,
  DialogFooterComponent,
  InputComponent,
  LabelComponent,
  FormFieldComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
  IconComponent,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@core/user-interface-legacy';

import type {
  JobSchedulerDialogData,
  JobSchedulerDialogResult,
} from './job-scheduler-dialog.types';
import type { JobValidationErrors } from '../../types/job-validation-errors.types';

@Component({
  selector: 'rt-job-scheduler-dialog',
  templateUrl: './job-scheduler-dialog.component.html',
  styleUrls: ['./job-scheduler-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    InfoboxComponent,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    OverlayOriginDirective,
    TimeZonePickerComponent,
    DialogFooterComponent,
    ButtonComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    DatePickerComponent,
    NgTemplateOutlet,
    IconComponent,
  ],
})
export class JobSchedulerDialogComponent
  implements DialogComponentI<JobSchedulerDialogData, JobSchedulerDialogResult>
{
  public editMode: boolean = false;
  public disablePublish: boolean = false;
  public publishStartDateView: string;
  public closeStartDateView: string;
  public form = this.fb.group({
    publishDate: this.fb.control(''),
    closeDate: this.fb.control(''),
    timezone: this.fb.control(''),
  });
  public timezoneDropdownPosition: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];
  public publishDatePickerOpen = false;
  public closeDatePickerOpen = false;
  public scheduleButtonLabel: string;

  public texts = {
    cancel: this.i18n.translate('app.common.cancel'),
    clear: this.i18n.translate('app.common.clear'),
    closeDate: this.i18n.translate('app.job_scheduler.close_date'),
    error: this.i18n.translate('app.job_scheduler.dialog.error'),
    headingCreate: this.i18n.translate('app.job_scheduler.dialog.heading_create'),
    headingEdit: this.i18n.translate('app.job_scheduler.dialog.heading_edit'),
    helpTooltip: this.i18n.translate('app.job_scheduler.dialog.help_tooltip'),
    infobox: this.i18n.translate('app.job_scheduler.dialog.infobox'),
    now: this.i18n.translate('app.job_scheduler.dialog.now'),
    placeholder: this.i18n.translate('app.job_scheduler.dialog.not_scheduled'),
    publish: this.i18n.translate('app.offer_form.publish'),
    publishDate: this.i18n.translate('app.job_scheduler.publish_date'),
    save: this.i18n.translate('app.common.save'),
    schedule: this.i18n.translate('app.job_scheduler.dialog.schedule'),
    subheading: `${this.i18n.translate(
      'app.job_scheduler.dialog.subheading',
    )} <a target="_blank" href="${this.i18n.translate(
      'app.support_resources.job_scheduler',
    )}">${this.i18n.translate('app.common.learn_more')}</a>`,
    infoboxFormInvalid: this.i18n.translate('app.job_scheduler.dialog.infobox_form_invalid'),
    infoboxExceededLocationsLimit: this.i18n.translate(
      'app.job_scheduler.dialog.infobox_exceeded_locations_limit',
    ),
    infoboxExceededCustomFieldsLimit: this.i18n.translate(
      'app.job_scheduler.dialog.infobox_exceeded_custom_fields_limit',
    ),
    infoboxRequisitionMissing: this.i18n.translate(
      'app.job_scheduler.dialog.infobox_requisition_missing',
    ),
    infoboxFormInvalidRequisitionMissing: this.i18n.translate(
      'app.job_scheduler.dialog.infobox_form_invalid_requisition_missing',
    ),
  };

  private jobId: number;
  private jobEditorValidationErrors: JobValidationErrors;

  constructor(
    private i18n: I18nService,
    private dialog: DialogRef,
    private fb: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef,
    private timeService: TimeService,
    private router: Router,
  ) {}

  public get displayPublishDate(): string {
    const date = this.publishControl.value || '';
    if (!date) return '';
    if (this.timeService.isToday(date)) return this.texts.now;

    return moment(date).format('DD MMM YYYY');
  }

  public get displayCloseDate(): string {
    const date = this.closeControl.value || '';
    if (!date) return '';

    return moment(date).format('DD MMM YYYY');
  }

  public get isValid(): boolean {
    if (!!this.publishDate && this.jobEditorValidationErrors) return false;
    if (this.disablePublish && !this.editMode) return !!this.closeDate;

    return true;
  }

  public get publishDate(): string {
    return this.publishControl.value || '';
  }

  public get closeDate(): string {
    return this.closeControl.value || '';
  }

  public get timezone(): string {
    return this.timezoneControl.value || '';
  }

  public get isValidationErrorInfoboxVisible(): boolean {
    return !!this.publishDate && this.jobEditorValidationErrors !== null;
  }

  public get validationErrorInfoboxText(): string {
    if (!this.jobEditorValidationErrors) return '';
    if (
      !!this.jobEditorValidationErrors.formErrorType &&
      this.jobEditorValidationErrors.isRequisitionMissing
    )
      return this.texts.infoboxFormInvalidRequisitionMissing;
    if (
      !!this.jobEditorValidationErrors.formErrorType ||
      this.jobEditorValidationErrors.isActiveValidLocationMissing ||
      this.jobEditorValidationErrors.invalidEmploymentType
    )
      return this.texts.infoboxFormInvalid;
    if (this.jobEditorValidationErrors.isRequisitionMissing)
      return this.texts.infoboxRequisitionMissing;
    if (this.jobEditorValidationErrors.exceededLocationsLimit)
      return this.texts.infoboxExceededLocationsLimit;
    if (this.jobEditorValidationErrors.exceededCustomFieldsLimit)
      return this.texts.infoboxExceededCustomFieldsLimit;
    return '';
  }

  public get isScheduledForToday() {
    return this.timeService.isToday(this.publishDate);
  }

  private get publishControl(): UntypedFormControl {
    return <UntypedFormControl>this.form.get('publishDate');
  }

  private get closeControl(): UntypedFormControl {
    return <UntypedFormControl>this.form.get('closeDate');
  }

  private get timezoneControl(): UntypedFormControl {
    return <UntypedFormControl>this.form.get('timezone');
  }

  public setDialogData(data: JobSchedulerDialogData, triggerUpdate: boolean): void {
    const { schedulerData, disablePublish, userTimezone, validationErrors, jobId } = data;

    this.publishStartDateView = moment().add(1, 'day').format('YYYY-MM-DD');
    this.closeStartDateView = moment(schedulerData?.publishDate ?? undefined)
      .add(1, 'day')
      .format('YYYY-MM-DD');

    if (schedulerData?.publishDate || schedulerData?.closeDate) this.editMode = true;
    this.form.patchValue({
      publishDate: schedulerData?.publishDate || '',
      closeDate: schedulerData?.closeDate || '',
      timezone: schedulerData?.timezone || userTimezone,
    });

    this.disablePublish = !!disablePublish;
    this.jobEditorValidationErrors = validationErrors || null;
    this.jobId = jobId;
    this.setScheduleButtonLabel();
  }

  public isCloseDateDisabled(closeDate: moment.Moment): boolean {
    const publish = this.publishDate;
    if (!publish) return false;
    return closeDate.isBefore(moment(publish).add(1, 'day'));
  }

  public isPublishDateDisabled(publishDate: moment.Moment): boolean {
    const close = this.closeDate || '';
    if (!close) return false;
    return publishDate.isAfter(moment(close));
  }

  public save(): void {
    this.dialog.close({
      save: true,
      schedulerData: {
        publishDate: this.publishDate,
        closeDate: this.closeDate,
        timezone: this.timezone,
      },
    });
  }

  public cancel(): void {
    this.dialog.close({ save: false });
  }

  public togglePublishDatePicker(): void {
    this.publishDatePickerOpen = !this.publishDatePickerOpen;
    this.cdRef.detectChanges();
  }

  public toggleCloseDatePicker(): void {
    this.closeDatePickerOpen = !this.closeDatePickerOpen;
    this.cdRef.detectChanges();
  }

  public closePickers(): void {
    this.publishDatePickerOpen = false;
    this.closeDatePickerOpen = false;
    this.cdRef.detectChanges();
  }

  public setPublish(publishDate: string | string[] | null): void {
    this.form.patchValue({ publishDate });

    this.closeStartDateView = moment(publishDate || undefined)
      .add(1, 'day')
      .format('YYYY-MM-DD');
    this.setScheduleButtonLabel();
  }

  public setClose(closeDate: string | string[] | null): void {
    this.form.patchValue({ closeDate });
  }

  public setTimezone(timezone: string): void {
    this.form.patchValue({ timezone });
  }

  public onInfoboxClick(mouseEvent: MouseEvent, element: HTMLElement | null): void {
    if (
      element?.tagName.toLowerCase() === 'a' &&
      this.jobEditorValidationErrors?.isRequisitionMissing
    ) {
      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();
      this.router
        .navigate([{ outlets: { 'job-editor': [this.jobId, 'job_details'] } }])
        .then(() => this.cancel());
    }
  }

  private setScheduleButtonLabel() {
    if (this.isScheduledForToday) {
      this.scheduleButtonLabel = this.texts.publish;
      return;
    }

    this.scheduleButtonLabel = this.editMode ? this.texts.save : this.texts.schedule;
  }
}
